import { RestRequest, RestContext, ResponseFunction } from 'msw'
import {
  addToEntityListData,
  updateEntityListData,
  deleteEntityListData,
} from '../../modules/mockDataCommon'
import { getTeamMemberDataForUserId } from '../teamMembers/mockData'
import { requireAuthenticatedUser } from '../../decorators'
import { getUserListData } from './mockData'
import { postUserAuthentication } from './authenticate'
import type { IUser } from '~/types/user/models'

interface GetUsersResponse {
  data: IUser[] | undefined
}

async function getUsers(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  console.log(`'Returning mock users.`)
  const responseBody: GetUsersResponse = { data: await getUserListData() }
  return res(ctx.status(200), ctx.json(responseBody))
}

async function getUser(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params

  // Note: the endpoint for getCurrentUser is /api/users/current, which
  // is exactly the same as the endpoint to get a user by id
  // (/api/users/:id). So an api call to get the current user ends
  // up here.

  const users = await getUserListData()
  const user = users.find(
    (u: IUser) => u.id == (id === 'current' ? 'XXU0005' : id)
  )
  if (user) {
    return res(ctx.status(200), ctx.json({ data: user }))
  } else {
    return res(ctx.status(404), ctx.json({ message: 'Resource not found.' }))
  }
}

async function getTeamMemberForUserId(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  const teamMember = await getTeamMemberDataForUserId(id)
  if (teamMember) {
    return res(ctx.status(200), ctx.json({ data: teamMember }))
  } else {
    return res(ctx.status(404), ctx.json({ message: 'Resource not found.' }))
  }
}

async function postUserLogout(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  // API server invalidates user authentication token,
  // and kills auth token cookie.  web app should handle
  // the response by sending user to the login page.
  return res(ctx.status(200))
}

// post

const postUser = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return addToEntityListData(req, res, ctx, getUserListData)
}

const patchUser = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return updateEntityListData(req, res, ctx, getUserListData)
}

const deleteUser = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return deleteEntityListData(req, res, ctx, getUserListData)
}

// Routes that require a valid x-user-token. Each is wrapped by
// requireAuthenticatedUser below.
const authenticatedHandlers = [
  {
    method: 'get',
    path: '/api/users',
    handler: getUsers,
  },
  {
    method: 'get',
    path: '/api/users/:id',
    handler: getUser,
  },
  {
    method: 'get',
    path: '/api/users/:id/teammember',
    handler: getTeamMemberForUserId,
  },
  {
    method: 'post',
    path: '/api/users',
    handler: postUser,
  },
  {
    method: 'post',
    path: '/api/users/logout',
    handler: postUserLogout,
  },
  {
    method: 'patch',
    path: '/api/users/:id',
    handler: patchUser,
  },
  {
    method: 'delete',
    path: '/api/users/:id',
    handler: deleteUser,
  },
].map((h) => ({ ...h, ...{ handler: requireAuthenticatedUser(h.handler) } }))

// Public routes — must NOT be wrapped by requireAuthenticatedUser, since the
// caller has no token yet (that's the whole point of authenticating).
const publicHandlers = [
  {
    method: 'post',
    path: '/api/users/authentication',
    handler: postUserAuthentication,
  },
]

export const handlers = [...publicHandlers, ...authenticatedHandlers]
